import { createSlice } from '@reduxjs/toolkit'
import {
    initializeAuthThunk,
    ensureAccessTokenThunk,
    loadPersistedAuthThunk,
    tokenRequestThunk,
    tokenRefreshThunk,
    profileRequestThunk,
    profileUpdateThunk,
    logoutThunk,
    avatarUploadThunk,
} from './userThunks'

const getErrorFromPayload = (payload) =>
  payload?.http_status_code
    ? `http_error_${payload.http_status_code}` 
    : 'server_error';

const initialUserState = {
  loading: {
    profile: false,
    update: false,
    avatar: false,
    auth: false
  },
  token_access: undefined,
  token_refresh: undefined,
  authInitialized: false,
  error: '',
  profile: {},
}

export const userSlice = createSlice({
    name: 'user',
    initialState: initialUserState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(initializeAuthThunk.pending, (state) => {
            state.loading.auth = true
            state.authInitialized = false
        })
        builder.addCase(initializeAuthThunk.fulfilled, (state, action) => {
            state.token_access = action.payload.access
            state.token_refresh = action.payload.refresh
            state.loading.auth = false
            state.authInitialized = true
            state.error = ''
        })
        builder.addCase(initializeAuthThunk.rejected, (state, action) => {
            clearAuthState(state)
            state.error = getErrorFromPayload(action.payload)
            state.authInitialized = true
        })
        builder.addCase(ensureAccessTokenThunk.fulfilled, (state, action) => {
            if (action.payload?.access) {
                state.token_access = action.payload.access
            }
            if (action.payload?.refresh) {
                state.token_refresh = action.payload.refresh
            }
            state.error = ''
        })
        builder.addCase(ensureAccessTokenThunk.rejected, (state, action) => {
            if (action.payload?.code !== 'access_token_expired') {
                clearAuthState(state)
                state.error = getErrorFromPayload(action.payload)
            }
        })
        builder.addCase(loadPersistedAuthThunk.fulfilled, (state, action) => {
            state.token_access = action.payload.access
            state.token_refresh = action.payload.refresh
        })
        builder.addCase(tokenRequestThunk.pending, (state, action) => {
            if (!state.loading.auth) {
                state.loading.auth = true
                state.currentRequestId = action.meta.requestId
            }
        })
        builder.addCase(tokenRequestThunk.fulfilled, (state, action) => {
            setLogged(state, action)
            state.loading.auth = false
            state.authInitialized = true
        })
        builder.addCase(tokenRequestThunk.rejected, (state, action) => {
            state.error = getErrorFromPayload(action.payload)
            state.loading.auth = false
            state.token_refresh = ''
        })
        builder.addCase(tokenRefreshThunk.fulfilled, (state, action) => {
            setLogged(state, action)
            state.loading.auth = false
        })
        builder.addCase(tokenRefreshThunk.rejected, (state, action) => {
            clearAuthState(state)
            state.error = getErrorFromPayload(action.payload)
            state.authInitialized = true
        })
        builder.addCase(profileRequestThunk.pending, (state, action) => {
            state.loading.profile = true
        })
        builder.addCase(profileRequestThunk.fulfilled, (state, action) => {
            state.profile = action.payload.length > 0 ? action.payload[0] : {}
            state.loading.profile = false
        })
        builder.addCase(profileRequestThunk.rejected, (state, action) => {
            state.error = getErrorFromPayload(action.payload)
            state.loading.profile = false
        })
        builder.addCase(profileUpdateThunk.pending, (state, action) => {
            state.loading.update = true
        })
        builder.addCase(profileUpdateThunk.fulfilled, (state, action) => {
            state.profile = action.payload[0]
            state.loading.update = false
        })
        builder.addCase(profileUpdateThunk.rejected, (state, action) => {
            state.error = getErrorFromPayload(action.payload)
            state.loading.update = false
        })
        builder.addCase(logoutThunk.pending, (state) => {
            clearAuthState(state)
            state.authInitialized = true
        })
        builder.addCase(logoutThunk.fulfilled, (state) => {
            clearAuthState(state)
            state.authInitialized = true
        })
        builder.addCase(avatarUploadThunk.pending, (state, action) => {
            state.loading.avatar = true
        })
        builder.addCase(avatarUploadThunk.fulfilled, (state, action) => {
            state.profile = action.payload
            state.loading.avatar = false
        })
        builder.addCase(avatarUploadThunk.rejected, (state, action) => {
            state.error = getErrorFromPayload(action.payload)
            state.loading.avatar = false
        })
    }
})

function clearAuthState(state) {
    state.token_access = undefined
    state.token_refresh = undefined
    state.error = ''
    state.loading.auth = false
    state.profile = {}
}

function setLogged(state, data) {
    if (!data?.payload) {
        state.error = 'invalid_payload'
        state.loading.auth = false
        return
    }

    state.token_access = data.payload.access

    if (data.payload.refresh) {
        state.token_refresh = data.payload.refresh
    }

    state.error = ''
    state.loading.auth = false
}

// Selectors
export const selectUser = (state) => state.user
export const selectUserTokenRefresh = (state) => state.user.token_refresh
export const selectUserTokenAccess = (state) => state.user.token_access
export const selectUserProfile = (state) => state.user.profile
export const selectProfileUser = (state) => state.user.profile?.user || null
export const selectProfileAvatar = (state) => {
    const avatarPath = state.user.profile?.avatar || null;
    if (avatarPath) {
        // Reemplazar s3:9000 por localhost:9000 para desarrollo
        return avatarPath.replace('s3:9000', 'localhost:9000');
    }
    return avatarPath;
}
export const selectUserUsername = (state) => state.user.profile?.user?.username || ''
export const selectUserFirstName = (state) => state.user.profile?.user?.first_name || ''
export const selectUserLastName = (state) => state.user.profile?.user?.last_name || ''
export const selectUserEmail = (state) => state.user.profile?.user?.email || ''
export const selectUserIsAdmin = (state) => Boolean(state.user.profile?.user?.is_staff)
export const selectProfileLoading = (state) => state.user.loading.profile
export const selectUpdateLoading = (state) => state.user.loading.update
export const selectAvatarLoading = (state) => state.user.loading.avatar
export const selectAuthLoading = (state) => state.user.loading.auth
export const selectUserError = (state) => state.user.error

// Action creators are generated for each case reducer function
export default function userReducer(state, action) {
    const requestId = action.meta?.requestId;
    if (!action.type.startsWith('user/') || !requestId) {
        return userSlice.reducer(state, action);
    }
    if (action.type === logoutThunk.pending.type || action.type === tokenRequestThunk.pending.type) {
        return { ...userSlice.reducer(state, action), pendingRequests: { [requestId]: true } };
    }
    if (action.meta.requestStatus === 'pending') {
        return {
            ...userSlice.reducer(state, action),
            pendingRequests: { ...state?.pendingRequests, [requestId]: true },
        };
    }
    // A response started before logout must never restore the old session.
    if (!state?.pendingRequests?.[requestId]) return state;
    const pendingRequests = { ...state.pendingRequests };
    delete pendingRequests[requestId];
    return { ...userSlice.reducer(state, action), pendingRequests };
}
